import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import {
  agentV2TranscriptReducer,
  kAgentV2InitialTranscriptState,
  type AgentV2MessageAttachedFiles,
  type AgentV2TranscriptState,
} from '@/agent-v2/store/reducer'
import type { AgentV2ServerEventEnvelope, MatterArtifactVersionSelection } from '@/agent-v2/store/schemas'

export type AgentV2TranscriptsState = {
  transcriptsByConversationId: Record<string, AgentV2TranscriptState | undefined>
}

const initialState: AgentV2TranscriptsState = {
  transcriptsByConversationId: {},
}

export const agentV2TranscriptSlice = createSlice({
  name: 'agentV2TranscriptState',
  initialState,
  reducers: {
    turnSubmitted: (
      state,
      action: PayloadAction<{
        clientRequestId: string
        conversationId: string
        inputText: string
        attachedSkillNames?: string[]
        // Accepted with the turn for analytics. The transcript message keeps attachedSkillNames only.
        selectedSkillIds?: string[]
        selectedPaxtonSkillNames?: string[]
        // Accepted with the turn for analytics. The transcript does not keep it.
        tableId?: string
        matterArtifactSelection?: MatterArtifactVersionSelection
        attachedFiles?: AgentV2MessageAttachedFiles
        userMessageId: string
        assistantMessageId: string
      }>
    ) => {
      const { conversationId, clientRequestId, inputText, attachedSkillNames, matterArtifactSelection, attachedFiles, userMessageId, assistantMessageId } =
        action.payload
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'turnSubmitted',
        clientRequestId,
        inputText,
        attachedSkillNames,
        matterArtifactSelection,
        attachedFiles,
        userMessageId,
        assistantMessageId,
      })
    },
    restoreAttachedSkillNames: (state, action: PayloadAction<{ conversationId: string; namesByRequestId: Record<string, string[]> }>) => {
      const transcript = state.transcriptsByConversationId[action.payload.conversationId]
      if (!transcript) return
      for (const message of transcript.messages) {
        if (message.role !== 'user' || !message.clientRequestId) continue
        const names = action.payload.namesByRequestId[message.clientRequestId]
        if (names) message.attachedSkillNames = names
      }
    },
    serverEventReceived: (state, action: PayloadAction<{ conversationId: string; event: AgentV2ServerEventEnvelope }>) => {
      const { conversationId, event } = action.payload
      if (event.conversation_id !== conversationId) return
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'serverEventReceived',
        event,
      })
    },
    transportDetached: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'transportDetached',
      })
    },
    turnFailedBeforeAdmission: (state, action: PayloadAction<{ conversationId: string; message: string }>) => {
      const { conversationId, message } = action.payload
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'turnFailedBeforeAdmission',
        message,
      })
    },
    runStoppedBeforeStart: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'runStoppedBeforeStart',
      })
    },
    matterArtifactEditAccepted: (
      state,
      action: PayloadAction<{
        conversationId: string
        turnId: string
        resultMatterArtifactVersionId: string
      }>
    ) => {
      const { conversationId, ...acceptedEdit } = action.payload
      state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
        type: 'matterArtifactEditAccepted',
        ...acceptedEdit,
      })
    },
    clearConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      delete state.transcriptsByConversationId[action.payload.conversationId]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AgentV2ConversationsActions.setConversationDetail, (state, action) => {
        const conversationId = action.payload.conversation.id
        state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
          type: 'hydrateFromPersistedConversation',
          conversation: action.payload,
        })
      })
      .addCase(AgentV2ConversationsActions.deleteConversation, (state, action) => {
        delete state.transcriptsByConversationId[action.payload.conversationId]
      })
      .addCase(AgentV2ConversationsActions.olderConversationHistoryReceived, (state, action) => {
        const conversationId = action.payload.conversation.id
        state.transcriptsByConversationId[conversationId] = agentV2TranscriptReducer(getTranscript(state, conversationId), {
          type: 'prependPersistedHistory',
          conversation: action.payload,
        })
      })
      .addCase(AgentV2ConversationsActions.nullifyData, () => initialState)
  },
})

function getTranscript(state: AgentV2TranscriptsState, conversationId: string): AgentV2TranscriptState {
  return state.transcriptsByConversationId[conversationId] ?? kAgentV2InitialTranscriptState
}

export const AgentV2TranscriptActions = agentV2TranscriptSlice.actions

export default agentV2TranscriptSlice.reducer
